import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/auth/AuthProvider";
import {
  claimReceivedLine,
  reportBondClaim,
  chosenBond,
  issuedBondOptions,
  type IssuedBondChoice,
} from "@/lib/bondServicing";
import { anchorServerError } from "@/lib/serverFieldErrors";
import { ActionFormDialog } from "@/components/ActionFormDialog";
import { NumericField } from "@/components/NumericField";
import { TextAreaField } from "@/components/TextAreaField";
import { TextField } from "@/components/TextField";
import {
  IssuedBondField,
  IssuedBondsNotice,
} from "@/components/bond-submission/IssuedBondField";

// BRK-S412 — "Report a claim" against an issued bond (PMS-S851):
// the claimant, the amount claimed — grouped as it is typed, a plain number on
// the wire (D-10 rule 3) — and what happened. The confirmation is PMS's own
// line from the 2xx; a refusal renders inline with the inputs kept.
//
// SHARED: the Surety account's Actions menu opens it over every issued bond;
// the submission page's action row (BRK-S428) passes its own `submissionId`
// and, when it knows one, the `bondId` to start on.

const FIELDS = ["bondId", "claimant", "amount", "description"] as const;
const DESCRIPTION_MAX = 4000;

export const CLAIMS_TEAM_NOTE =
  "Our claims team will contact the principal and obligee within 2 business days.";

export function ReportClaimDialog({
  open,
  onClose,
  bonds,
  bondsError = null,
  submissionId,
  bondId,
  onReported,
}: {
  open: boolean;
  onClose: () => void;
  /** The issued bonds to offer; null while they are read. */
  bonds: readonly IssuedBondChoice[] | null;
  bondsError?: string | null;
  /** Narrow the bonds to one submission (the submission page). */
  submissionId?: string;
  /** The bond to start on. */
  bondId?: string;
  /** After the 2xx only: the submission page re-reads its order so the
   *  history row comes from the server (BRK-S428). */
  onReported?: () => void;
}) {
  const { authedRequest } = useAuth();
  const { options, initial } = useMemo(
    () => issuedBondOptions(bonds ?? [], submissionId, bondId),
    [bonds, submissionId, bondId],
  );
  const [bondKey, setBondKey] = useState(initial);
  const [claimant, setClaimant] = useState("");
  const [amount, setAmount] = useState<number | undefined>(undefined);
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invalid, setInvalid] = useState<Record<string, string>>({});
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setBondKey(initial);
    setClaimant("");
    setAmount(undefined);
    setDescription("");
    setError(null);
    setInvalid({});
    setSuccess(null);
  }, [open, initial]);

  async function send() {
    const bond = chosenBond(options, bondKey);
    const who = claimant.trim();
    const what = description.trim();
    const missing: Record<string, string> = {};
    if (!bond) missing.bondId = "Choose a bond.";
    if (who === "") missing.claimant = "Enter who is making the claim.";
    if (amount === undefined) missing.amount = "Enter the amount claimed.";
    else if (!(amount > 0)) missing.amount = "Enter an amount above $0.";
    if (what === "") missing.description = "Describe what happened.";
    setInvalid(missing);
    setError(null);
    if (!bond || amount === undefined || Object.keys(missing).length > 0) {
      return;
    }
    setBusy(true);
    try {
      const receipt = await reportBondClaim(authedRequest, bond.submissionId, {
        bondId: bond.bondId,
        claimant: who,
        amount,
        description: what,
      });
      setSuccess(claimReceivedLine(receipt));
      onReported?.();
    } catch (err) {
      const anchored = anchorServerError(err, [...FIELDS]);
      setInvalid(anchored.byField);
      setError(anchored.summary);
    } finally {
      setBusy(false);
    }
  }

  const ready = bonds !== null && options.length > 0;
  return (
    <ActionFormDialog
      open={open}
      onClose={onClose}
      title="Report a claim"
      lead="Report a claim so our claims team can open a file."
      submitLabel="Report claim"
      busy={busy}
      error={error}
      success={success}
      canSubmit={ready}
      onSubmit={() => void send()}
    >
      <IssuedBondsNotice
        bonds={bonds === null ? null : options}
        bondsError={bondsError}
        emptyText={`There are no issued bonds on this ${
          submissionId === undefined ? "account" : "submission"
        } yet, so there’s nothing to claim against.`}
      />
      {ready && (
        <>
          <IssuedBondField
            options={options}
            value={bondKey}
            onChange={setBondKey}
            error={invalid.bondId}
          />
          <TextField
            label="Claimant"
            required
            placeholder="Who is making the claim"
            value={claimant}
            onChange={(e) => setClaimant(e.target.value)}
            error={invalid.claimant}
          />
          <NumericField
            kind="currency"
            label="Amount claimed"
            required
            value={amount}
            onChange={setAmount}
            error={invalid.amount}
          />
          <TextAreaField
            label="What happened"
            required
            maxLength={DESCRIPTION_MAX}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            error={invalid.description}
          />
          <p className="text-sm text-brk-dim">{CLAIMS_TEAM_NOTE}</p>
        </>
      )}
    </ActionFormDialog>
  );
}
